import { createSlice } from "@reduxjs/toolkit";
import { resetAllState } from "./resetAllState";
import {
  ApprovalsState,
  approvalsInitialState,
  approvalsReducers,
  attachApprovalsCases,
} from "./admin/approvals";
import {
  AdminSubscriptionsState,
  attachSubscriptionsCases,
  subscriptionsInitialState,
  subscriptionsReducers,
} from "./admin/subscriptions";
import {
  AllBusinessesState,
  allBusinessesInitialState,
  allBusinessesReducers,
  attachAllBusinessesCases,
} from "./admin/allBusinesses";
import {
  AdminSettlementsState,
  attachSettlementsCases,
  settlementsInitialState,
  settlementsReducers,
} from "./admin/settlements";

/**
 * The Admin store.
 *
 * ONE slice, one sub-object per admin tab (`state.admin.approvals`,
 * `state.admin.subscriptions`, ...). Adding a tab is: write its file under
 * ./admin/, then add two lines here - its initial state and its attacher.
 * This file is meant to stay short no matter how many tabs exist; if a tab's
 * reducers ever start appearing HERE, they're in the wrong place.
 *
 * The one thing a single slice can't enforce: all tabs share one Immer draft,
 * so nothing stops a tab's reducer writing another tab's sub-object. The rule
 * is that an attacher only ever touches `state.<its own key>`.
 */
export interface AdminState {
  approvals: ApprovalsState;
  subscriptions: AdminSubscriptionsState;
  allBusinesses: AllBusinessesState;
  settlements: AdminSettlementsState;
}

const initialState = (): AdminState => ({
  approvals: approvalsInitialState(),
  subscriptions: subscriptionsInitialState(),
  allBusinesses: allBusinessesInitialState(),
  settlements: settlementsInitialState(),
});

const adminSlice = createSlice({
  name: "admin",
  initialState: initialState(),
  reducers: {
    ...approvalsReducers,
    ...subscriptionsReducers,
    ...allBusinessesReducers,
    ...settlementsReducers,
  },
  extraReducers: (builder) => {
    attachApprovalsCases(builder);
    attachSubscriptionsCases(builder);
    attachAllBusinessesCases(builder);
    attachSettlementsCases(builder);
    builder.addCase(resetAllState, () => initialState());
  },
});

export const {
  setApprovalStatus,
  setApprovalSearch,
  setApprovalSort,
  setSubscriptionSearch,
  setSubscriptionStatusFilter,
  setAllBusinessesExpired,
  setAllBusinessesSearch,
  setSettlementStatus,
} = adminSlice.actions;

export default adminSlice.reducer;
